// Users Controller
// Handles user-related operations

import { post } from './utilitiesController.js';

/**
 * Get a user based on provided data
 * @param {Object} data - Data to identify the user
 * @returns {Object|null} - User object or null if not found
 */
export const getUser = (data) => {
    // TODO: Implement user retrieval logic
    // This function will contain the logic to retrieve a user
    // based on the provided data parameter
};


/**
 * Register a user with email
 * @param {Object} data - Data containing email
 * @returns {Promise} - Response from registration endpoint
 */
export const register = async (data) => {
    try {
        const response = await post('api/register', data);
        return response;
    } catch (error) {
        console.error('Registration failed:', error);
        throw error;
    }
};

/**
 * Login a user with email
 * @param {Object} data - Data containing email
 * @returns {Promise} - Response from login endpoint
 */
export const login = async (data) => {
    try {
        const response = await post('api/login', data);

        return response;
    } catch (error) {
        console.error('Login failed:', error);
        throw error;
    }
};

/**
 * Verify OTP token
 * @param {Object} data - Data containing token
 * @returns {Promise} - Response from verification endpoint
 */
export const verifyOTP = async (data) => {
    try {
        const response = await post('api/verify-login', data);

        console.log('login response', response);

        if(response.data.success) {
            localStorage.setItem('token', response.data.jwt_token);
        }

        return response;
    } catch (error) {
        console.error('OTP verification failed:', error);
        throw error;
    }
};

/**
 * Create a new user access role
 * @param {Object} data - Data containing role name
 * @returns {Promise} - Response from role creation endpoint
 */
export const createUserAccessRole = async (data) => {
    try {
        const response = await post('api/user-access-roles/create', data, true);
        return response;
    } catch (error) {
        console.error('User access role creation failed:', error);
        throw error;
    }
};

export const updateUserAccessRole = async (data) => {
    try {
        const response = await post('api/user-access-roles/update', data, true);
        return response;
    } catch (error) {
        console.error('User access role update failed:', error);
        throw error;
    }
};

/**
 * Get list of user access roles
 * @returns {Promise} - Response from roles list endpoint
 */
export const getUserAccessRoles = async () => {
    try {
        const response = await post('api/user-access-roles/list', {}, true);
        return response;
    } catch (error) {
        console.error('Failed to fetch user access roles:', error);
        throw error;
    }
};

/**
 * Toggle archive status of a user access role
 * @param {Object} data - Data containing role_id
 * @returns {Promise} - Response from toggle archive endpoint
 */
export const toggleUserAccessRoleArchive = async (data) => {
    try {
        const response = await post('api/user-access-roles/toggle-archive', data, true);
        return response;
    } catch (error) {
        console.error('Failed to toggle user access role archive status:', error);
        throw error;
    }
};

/**
 * Create a new user
 * @param {Object} data - Data for the new user
 * @returns {Promise} - Response from user creation endpoint
 */
export const createUser = async (data) => {
    try {
        const response = await post('api/users/create', data, true);
        return response;
    } catch (error) {
        console.error('Failed to create user:', error);
        throw error;
    }
};

/**
 * Get list of users
 * @param {Object} params - Query parameters for pagination (page, limit)
 * @returns {Promise} - Response from users list endpoint
 */
export const getUsers = async (params = {}) => {
    try {
        const response = await post('api/users/list', params, true);
        return response;
    } catch (error) {
        console.error('Failed to fetch users:', error);
        throw error;
    }
};

/**
 * Update an existing user
 * @param {Object} data - Data for updating the user (must include userId)
 * @returns {Promise} - Response from user profile update endpoint
 */
export const updateUser = async (data) => {
    try {
        const response = await post('api/users/update', data, true);
        return response;
    } catch (error) {
        console.error('Failed to update user profile:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Get all functionalities
 * @returns {Promise} - Response from functionalities endpoint
 */
export const getAllFunctionalities = async () => {
    try {
        const response = await post('api/users/get_all_functionalities', {}, true);
        return response;
    } catch (error) {
        console.error('Failed to fetch functionalities:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
    }
};

export const createUserAccess = async (data) => {
    try {
        const response = await post('api/user-access/create', data, true);
        return response;
    } catch (error) {
        console.error('Failed to create user access:', error);
        throw error;
    }
};

export const getUserAccess = async (data) => {
    try {
        const response = await post('api/user-access/get', data, true);
        return response;
    } catch (error) {
        console.error('Failed to get user access:', error);
        throw error;
    }
};

export const updateUserAccess = async (data) => {
    try {
        const response = await post('api/user-access/update', data, true);
        return response;
    } catch (error) {
        console.error('Failed to update user access:', error);
        throw error;
    }
};

export const getUserIncubationAccess = async (data) => {
    try {
        const response = await post('api/user-incubation-access/get', data, true);
        return response;
    } catch (error) {
        console.error('Failed to get user incubation access:', error);
        throw error;
    }
};

export const getIncubationUserAccess = async (data) => {
    try {
        const response = await post('api/user-incubation-access/by-incubation', data, true);
        return response;
    } catch (error) {
        console.error('Failed to get incubation user access:', error);
        throw error;
    }
};

export const bulkSetUserIncubationAccess = async (data) => {
    try {
        const response = await post('api/user-incubation-access/bulk-set', data, true);
        return response;
    } catch (error) {
        console.error('Failed to bulk-set user incubation access:', error);
        throw error;
    }
};

export const upsertUserIncubationAccess = async (data) => {
    try {
        const response = await post('api/user-incubation-access/upsert', data, true);
        return response;
    } catch (error) {
        console.error('Failed to upsert user incubation access:', error);
        throw error;
    }
};

export const deleteUserIncubationAccess = async (data) => {
    try {
        const response = await post('api/user-incubation-access/delete', data, true);
        return response;
    } catch (error) {
        console.error('Failed to delete user incubation access:', error);
        throw error;
    }
};

/**
 * Get user profile by ID (Route 1)
 * @param {Object} data - Data containing userId
 * @returns {Promise} - Response from user profile get endpoint
 */
export const getUserProfileById = async (data) => {
    try {
        const response = await post('api/users/get_from_id', data, true);
        return response;
    } catch (error) {
        console.error('Failed to fetch user profile by ID:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Get current user's profile from JWT token (Route 2)
 * @returns {Promise} - Response from user profile get_from_auth endpoint
 */
export const getCurrentUserProfile = async () => {
    try {
        const response = await post('api/users/get_from_auth', {}, true);
        return response;
    } catch (error) {
        console.error('Failed to fetch current user profile:', error);
        console.error('Error details:', error.response?.data || error.message);
        throw error;
    }
};

export default {
    getUser,
    register,
    verifyOTP,
    createUserAccessRole,
    updateUserAccessRole,
    getUserAccessRoles,
    toggleUserAccessRoleArchive,
    createUser,
    getUsers,
    updateUser,
    getAllFunctionalities,
    createUserAccess,
    getUserAccess,
    updateUserAccess,
    getUserIncubationAccess,
    getIncubationUserAccess,
    bulkSetUserIncubationAccess,
    upsertUserIncubationAccess,
    deleteUserIncubationAccess,
    getUserProfileById,
    getCurrentUserProfile
};
